import {
  Sparkles,
  Users2,
  MessageCircle,
  HeartHandshake,
  ShieldCheck,
  ClipboardCheck,
  MapPin,
} from "lucide-react";

const pillars = [
  {
    icon: Users2,
    title: "Consistency",
    body: "We prioritise continuity of care by allocating the same Registered Nurse whenever possible. This supports stronger therapeutic relationships, clearer communication and a better understanding of each client's ongoing clinical needs.",
  },
  {
    icon: MessageCircle,
    title: "Responsiveness",
    body: "We provide direct and timely communication with participants, families, care partners, support coordinators and referring organisations. Clinical concerns are escalated appropriately, and relevant updates are communicated without unnecessary delays.",
  },
  {
    icon: HeartHandshake,
    title: "Holistic, Person-Centred Care",
    body: "We look beyond a single procedure or diagnosis. Our nursing care considers the person's physical health, mental wellbeing, functional needs, home environment, support network, personal goals and risk of avoidable hospitalisation.",
  },
];

const strengths = [
  { icon: ShieldCheck, text: "Registered Nurse-led care" },
  { icon: Users2, text: "Continuity of clinician" },
  { icon: ClipboardCheck, text: "Collaborative care planning" },
  { icon: Sparkles, text: "Clear clinical documentation" },
  { icon: MessageCircle, text: "Flexible referral pathways" },
  { icon: MapPin, text: "Canberra & nearby NSW coverage" },
];

export const WhyChoose = () => {
  return (
    <section
      id="why-us"
      data-testid="why-section"
      className="relative py-20 sm:py-28 bg-[var(--ew-bg)]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl">
          <p className="eyebrow">Why Choose Everwell</p>
          <h2 className="font-heading font-medium mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] text-[var(--ew-text)]">
            Professional clinical care with a personal touch.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[var(--ew-text-2)] leading-relaxed">
            Everwell combines professional clinical care with the flexibility,
            accountability and personal attention of a locally operated RN-led
            service.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <article
              key={p.title}
              data-testid={`pillar-${i}`}
              className="ew-card p-7 sm:p-8"
            >
              <div className="icon-badge">
                <p.icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-xl sm:text-[1.4rem] font-semibold mt-5 text-[var(--ew-text)] leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 text-[var(--ew-text-2)] leading-relaxed text-sm sm:text-base">
                {p.body}
              </p>
            </article>
          ))}
        </div>

        <div className="mt-12 rounded-3xl bg-white border border-[#6E9C88]/15 p-7 sm:p-9">
          <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[var(--ew-text-muted)]">
            Additional strengths
          </p>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {strengths.map((s, i) => (
              <li
                key={s.text}
                data-testid={`strength-${i}`}
                className="flex items-center gap-3 rounded-2xl border border-[#6E9C88]/15 bg-[var(--ew-bg-3)]/60 px-4 py-3"
              >
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--ew-sage)]/15 text-[var(--ew-accent)]">
                  <s.icon className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <span className="text-sm font-medium text-[var(--ew-text)]">
                  {s.text}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-[var(--ew-text-muted)] leading-relaxed">
            Experience across disability, aged care, post-hospital care, mental
            health and complex community nursing. Strong communication with
            healthcare and community care partners.
          </p>
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
